import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { SpKeywordApplyAction } from '@/types/keywordRecommendations';
import { actionTypeLabel } from '@/api/recommendationUtils';

type ApplyPreviewRow = {
  label: string;
  action: string;
  detail: string;
  bid?: number;
};

function buildPreviewRows(actions: SpKeywordApplyAction[]): ApplyPreviewRow[] {
  return actions.map((action) => {
    if (action.action === 'PAUSE') {
      return { label: action.keywordId || 'Keyword', action: 'Pause', detail: 'Pause keyword' };
    }
    if (
      action.action === 'INCREASE_BID' ||
      action.action === 'DECREASE_BID' ||
      action.action === 'SET_BID'
    ) {
      return {
        label: action.keywordId || 'Keyword',
        action: actionTypeLabel(action.action),
        detail: `New bid: ${action.bid?.toFixed(2) ?? '—'}`,
        bid: action.bid,
      };
    }
    if (action.action === 'ADD_KEYWORD') {
      return {
        label: action.keywordText || 'Keyword',
        action: 'Add keyword',
        detail: `${action.matchType || 'EXACT'} match`,
        bid: action.bid,
      };
    }
    if (action.action === 'ADD_NEGATIVE_KEYWORD') {
      return {
        label: action.keywordText || 'Keyword',
        action: 'Add negative',
        detail: action.matchType || 'NEGATIVE_EXACT',
      };
    }
    return { label: action.action, action: action.action, detail: '' };
  });
}

type KeywordApplyConfirmModalProps = {
  actions: SpKeywordApplyAction[];
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function KeywordApplyConfirmModal({
  actions,
  pending,
  onConfirm,
  onCancel,
}: KeywordApplyConfirmModalProps) {
  const rows = buildPreviewRows(actions);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">Apply recommendations</h3>
        <p className="mt-1 text-sm text-foreground-muted">
          Review changes before applying to your Amazon Ads account.
        </p>

        <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="border-b border-border bg-surface-muted">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-semibold text-foreground-muted">
                  Item
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold text-foreground-muted">
                  Action
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold text-foreground-muted">
                  Detail
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-foreground-muted">
                  Bid
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row, index) => (
                <tr key={`${row.label}-${index}`}>
                  <td className="px-3 py-2 font-medium text-foreground">{row.label}</td>
                  <td className="px-3 py-2 text-foreground-muted">{row.action}</td>
                  <td className="px-3 py-2 text-foreground-muted">{row.detail}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.bid != null ? row.bid.toFixed(2) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs text-foreground-subtle">
          {actions.length} action{actions.length === 1 ? '' : 's'} will be sent to Amazon.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={pending || actions.length === 0}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
